const EXAMPLE_IMAGE_RE = /^https?:\/\/(www\.)?example\.com\//i;
const API_BASE = process.env.VUE_APP_BASE_URL || "/wx";
const IMAGE_PROXY_BASE = `${API_BASE}/media/image?url=`;
const WORK_THUMBNAIL_PROCESS = "image/resize,w_720/format,webp/quality,Q_75";

function normalizePathSegment(segment) {
  if (!segment) {
    return segment;
  }
  try {
    return encodeURIComponent(decodeURIComponent(segment));
  } catch (error) {
    return encodeURIComponent(segment);
  }
}

function normalizeImageUrl(url) {
  const value = String(url || "").trim();
  if (!value || /^data:image\//i.test(value)) {
    return value;
  }
  const protocolRelative = /^\/\//.test(value);
  const normalizedValue = (protocolRelative ? `https:${value}` : value)
    .replace(/^http:\/\/([^/]*aliyuncs\.com)(?=\/)/i, "https://$1");
  if (hasOssSignature(normalizedValue)) {
    return normalizedValue;
  }
  const match = normalizedValue.match(/^(https?:\/\/[^/?#]+)?([^?#]*)(\?[^#]*)?(#.*)?$/i);
  if (!match) {
    return normalizedValue;
  }
  const [, origin = "", path = "", query = "", hash = ""] = match;
  const normalizedPath = path
    .split("/")
    .map((segment, index) => (index === 0 ? segment : normalizePathSegment(segment)))
    .join("/");
  return `${origin}${normalizedPath}${query}${hash}`;
}

function isAliOssUrl(url) {
  if (!url) {
    return false;
  }
  try {
    return /aliyuncs\.com$/i.test(new URL(String(url).trim()).hostname);
  } catch (error) {
    return false;
  }
}

function hasOssSignature(url) {
  return /[?&](?:OSSAccessKeyId|Expires|Signature|x-oss-signature|x-oss-credential)=/i.test(url);
}

export function toDirectImageUrl(url) {
  return normalizeImageUrl(url);
}

export function toDisplayImageUrl(url) {
  const value = normalizeImageUrl(url);
  if (!value) {
    return "";
  }
  if (!isAliOssUrl(value)) {
    return value;
  }
  return `${IMAGE_PROXY_BASE}${encodeURIComponent(value)}`;
}

export function toWorkThumbnailUrl(url) {
  const value = normalizeImageUrl(url);
  if (!value || !isAliOssUrl(value)) {
    return value;
  }
  if (/[?&]x-oss-process=/i.test(value) || hasOssSignature(value)) {
    return value;
  }

  const hashIndex = value.indexOf("#");
  const hash = hashIndex >= 0 ? value.slice(hashIndex) : "";
  const urlWithoutHash = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const separator = urlWithoutHash.includes("?") ? "&" : "?";
  return `${urlWithoutHash}${separator}x-oss-process=${WORK_THUMBNAIL_PROCESS}${hash}`;
}

function splitImageValue(value) {
  if (!value) {
    return [];
  }
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseExtraJson(value) {
  if (!value) {
    return null;
  }
  try {
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch (error) {
    return null;
  }
}

function isUsableImage(url) {
  if (!url) {
    return false;
  }
  const value = String(url).trim();
  if (!value) {
    return false;
  }
  return !EXAMPLE_IMAGE_RE.test(value);
}

function resolveWorkImageCandidates(work) {
  const extra = parseExtraJson(work && work.extraJson);
  const candidates = []
    .concat(Array.isArray(work && work.imageArr) ? work.imageArr : [])
    .concat(splitImageValue(work && work.image))
    .concat(Array.isArray(extra?.work?.imageList) ? extra.work.imageList : [])
    .concat(splitImageValue(extra?.work?.images));

  return candidates.filter((item, index) => {
    if (!isUsableImage(item)) {
      return false;
    }
    return candidates.indexOf(item) === index;
  });
}

export function resolveWorkImageList(work) {
  return resolveWorkImageCandidates(work).map((item) => toDisplayImageUrl(item));
}

export function resolveWorkDirectImageList(work) {
  return resolveWorkImageCandidates(work).map((item) => toDirectImageUrl(item));
}

export function resolveWorkCover(work, fallbackImage) {
  const imageList = resolveWorkImageList(work);
  return imageList[0] || fallbackImage;
}

export function resolveWorkOriginal(work, fallbackImage) {
  const imageList = resolveWorkImageCandidates(work);
  return toDirectImageUrl(imageList[0]) || fallbackImage;
}

export function resolveWorkThumbnail(work, fallbackImage) {
  const imageList = resolveWorkImageCandidates(work);
  return toWorkThumbnailUrl(imageList[0]) || fallbackImage;
}

export function resolveWorkThumbnailCandidates(work) {
  return resolveWorkImageCandidates(work).reduce((result, image) => {
    const thumbnail = toWorkThumbnailUrl(image);
    const original = toDirectImageUrl(image);
    [thumbnail, original].forEach((candidate) => {
      if (candidate && !result.includes(candidate)) {
        result.push(candidate);
      }
    });
    return result;
  }, []);
}

export function replaceBrokenImage(event, fallbackImage, retryImage) {
  if (!event || !event.target) {
    return;
  }
  const target = event.target;
  const retryUrl = toDirectImageUrl(retryImage);
  if (
    retryUrl
    && target.dataset
    && target.dataset.originalRetryApplied !== "1"
    && target.src !== retryUrl
  ) {
    target.dataset.originalRetryApplied = "1";
    target.src = retryUrl;
    return;
  }
  if (target.dataset && target.dataset.fallbackApplied === "1") {
    return;
  }
  if (target.dataset) {
    target.dataset.fallbackApplied = "1";
  }
  target.src = fallbackImage;
}

export function replaceBrokenImageFromCandidates(event, candidates, fallbackImage) {
  if (!event || !event.target) {
    return;
  }
  const target = event.target;
  const list = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
  const currentIndex = Number(target.dataset?.candidateIndex || 0);
  const nextIndex = currentIndex + 1;
  if (nextIndex < list.length) {
    if (target.dataset) {
      target.dataset.candidateIndex = String(nextIndex);
    }
    target.src = list[nextIndex];
    return;
  }
  replaceBrokenImage(event, fallbackImage);
}
